/**
 * Converts a single chart from the editor's internal format to v2 schema format.
 * The editor uses flat fields (bar_metric, bar1_metric, etc.) while v2 uses a metrics array.
 */
function editorChartToV2(chart) {
    const type = chart.type;
    const v2 = { type, title: chart.title || '', metrics: [] };

    switch (type) {
        case 'bar_chart':
            if (chart.metric) {
                v2.metrics.push({ key: chart.metric, role: 'bar' });
            }
            break;

        case 'bar_and_bubble_chart':
            if (chart.bar_metric) {
                v2.metrics.push({ key: chart.bar_metric, role: 'bar' });
            }
            if (chart.bubble_metric) {
                v2.metrics.push({ key: chart.bubble_metric, role: 'bubble' });
            }
            break;

        case 'grouped_bar_chart':
            if (chart.bar1_metric) {
                v2.metrics.push({ key: chart.bar1_metric, role: 'bar' });
            }
            if (chart.bar2_metric) {
                v2.metrics.push({ key: chart.bar2_metric, role: 'bar' });
            }
            break;

        case 'stacked_bar_chart':
            for (let i = 1; i <= 9; i++) {
                const key = `bar${i}_metric`;
                if (chart[key]) {
                    v2.metrics.push({ key: chart[key], role: 'bar' });
                }
            }
            if (chart.label_metric) {
                if (chart.label_metric === 'sum') {
                    v2.label = { type: 'sum' };
                } else {
                    v2.label = { type: 'metric', metric: chart.label_metric };
                }
            }
            break;

        case 'max_velo_chart':
            if (chart.perc_metric) {
                v2.metrics.push({ key: chart.perc_metric, role: 'perc' });
            }
            if (chart.effs_metric) {
                v2.metrics.push({ key: chart.effs_metric, role: 'effs' });
            }
            break;

        default:
            break;
    }

    return v2;
}

/**
 * Converts a single v2 chart back to the editor's internal format.
 */
function v2ChartToEditor(v2Chart) {
    const type = v2Chart.type;
    const chart = { type, title: v2Chart.title || '' };
    const metrics = v2Chart.metrics || [];

    switch (type) {
        case 'bar_chart': {
            const bar = metrics.find(m => m.role === 'bar');
            if (bar) chart.metric = bar.key;
            break;
        }

        case 'bar_and_bubble_chart': {
            const bar = metrics.find(m => m.role === 'bar');
            const bubble = metrics.find(m => m.role === 'bubble');
            if (bar) chart.bar_metric = bar.key;
            if (bubble) chart.bubble_metric = bubble.key;
            chart.subtitle = '';
            break;
        }

        case 'grouped_bar_chart': {
            const bars = metrics.filter(m => m.role === 'bar');
            bars.forEach((m, i) => {
                chart[`bar${i + 1}_metric`] = m.key;
            });
            chart.subtitle_1 = '';
            chart.subtitle_2 = '';
            break;
        }

        case 'stacked_bar_chart': {
            const bars = metrics.filter(m => m.role === 'bar');
            bars.forEach((m, i) => {
                chart[`bar${i + 1}_metric`] = m.key;
            });
            const label = v2Chart.label;
            if (label) {
                if (label.type === 'sum') {
                    chart.label_metric = 'sum';
                } else if (label.type === 'metric') {
                    chart.label_metric = label.metric;
                }
            }
            break;
        }

        case 'max_velo_chart': {
            const perc = metrics.find(m => m.role === 'perc');
            const effs = metrics.find(m => m.role === 'effs');
            if (perc) chart.perc_metric = perc.key;
            if (effs) chart.effs_metric = effs.key;
            break;
        }

        default:
            break;
    }

    return chart;
}

/**
 * Converts the full editor state to the backend's expected format.
 * Extracts overview charts and converts them to v2 format for platform_charts,
 * while preserving the full editor state for the editor_state field.
 */
export function editorStateToApiPayload(editorState) {
    const overviewCharts = editorState?.overview?.charts || [];
    const v2Charts = overviewCharts
        .filter(c => c.type !== 'placeholder')
        .map(editorChartToV2);

    const hiddenMetrics = deriveHiddenMetrics(v2Charts);

    return {
        platform_charts: {
            version: 2,
            charts: v2Charts,
            hidden_metrics: hiddenMetrics,
        },
    };
}

/**
 * Derives which metrics should be hidden from default bar rendering.
 * Any metric that appears in a multi-metric chart should be hidden
 * so it doesn't also render as a standalone bar.
 */
function deriveHiddenMetrics(v2Charts) {
    const hidden = new Set();
    for (const chart of v2Charts) {
        const metrics = chart.metrics || [];
        if (chart.type === 'bar_and_bubble_chart') {
            for (const m of metrics) hidden.add(m.key);
        } else if (chart.type === 'stacked_bar_chart') {
            for (const m of metrics) hidden.add(m.key);
            if (chart.label?.type === 'metric' && chart.label.metric) {
                hidden.add(chart.label.metric);
            }
        } else if (chart.type === 'grouped_bar_chart') {
            for (const m of metrics) hidden.add(m.key);
        } else if (chart.type === 'max_velo_chart') {
            for (const m of metrics) hidden.add(m.key);
        }
    }
    return [...hidden];
}

/**
 * Converts the backend API response to the editor's internal state format.
 * If editor_state is present, use it directly (it's in editor format).
 * If only platform_charts is present, convert v2 charts to editor format.
 */
export function apiResponseToEditorState(response) {
    if (response.platform_charts && response.platform_charts.charts) {
        const editorCharts = response.platform_charts.charts.map(v2ChartToEditor);
        return {
            overview: {
                players: [],
                charts: editorCharts,
            },
        };
    }

    return null;
}
